package com.trevorwiebe.timesheet.invoicing.domain

import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import com.trevorwiebe.timesheet.invoicing.domain.model.Invoice
import com.trevorwiebe.timesheet.invoicing.domain.model.InvoiceStatus

/** Sent invoices for [customerId] that still have a balance, oldest first: the ones a check can pay. */
fun openInvoices(invoices: List<Invoice>, customerId: String): List<Invoice> =
    invoices
        .filter { it.customerId == customerId && it.status == InvoiceStatus.SENT && it.balance > 0 }
        .sortedWith(compareBy({ it.invoiceDate }, { it.number }))

/**
 * Splits [amount] across [invoices] in order, paying each in full before moving to the next.
 * A short check leaves the last invoice it reaches partly paid; anything beyond the total due is
 * left unapplied.
 */
fun allocateOldestFirst(amount: Int, invoices: List<Invoice>): Map<String, Int> {
    var remaining = amount.coerceAtLeast(0)
    return invoices.associate { invoice ->
        val applied = minOf(remaining, invoice.balance)
        remaining -= applied
        invoice.id to applied
    }
}

/**
 * Why a payment can't be saved yet, or null when it can. Every dollar must land on an invoice:
 * there are no customer credits, so a check bigger than what's owed has to be looked into first.
 */
fun paymentProblem(amount: Int, allocations: Map<String, Int>, invoices: List<Invoice>): String? {
    if (amount <= 0) return "Enter the payment amount."
    val byId = invoices.associateBy { it.id }
    for ((invoiceId, applied) in allocations) {
        val invoice = byId[invoiceId] ?: return "An invoice on this payment is no longer open."
        if (applied < 0) return "Amounts can't be negative."
        if (applied > invoice.balance) {
            return "Invoice ${invoice.numberLabel} only has ${invoice.balanceFormatted} left to pay."
        }
    }
    val applied = allocations.values.sum()
    if (applied == 0) return "Apply the payment to at least one invoice."
    val unapplied = amount - applied
    if (unapplied > 0) {
        return "$${unapplied.formatCentsAsDollars()} isn't applied to an invoice yet."
    }
    if (unapplied < 0) {
        return "You've applied $${(-unapplied).formatCentsAsDollars()} more than the payment amount."
    }
    return null
}
